import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo } from 'src/app/shared/models';
import { NewsLetterService } from 'src/app/services/news-letter/news-letter.service';
import { NewsLetterModalService } from 'src/app/services/news-letter-modal/news-letter-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-news-letter-listing',
  templateUrl: './news-letter-listing.component.html',
  styleUrls: ['./news-letter-listing.component.scss']
})
export class NewsLetterListingComponent extends DestructorComponent implements OnInit {

  @Output() newsLetterAdded = new EventEmitter<void>();
  @Output() newsLetterUpdated = new EventEmitter<void>();

  newsLetters: any[] = [];
  isLoading = false;
  currentPage = 1;
  searchTerm = '';

  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  filters: any = {
  };

  constructor(
    private _newsLetterService: NewsLetterService,
    private _newsLetterModalService: NewsLetterModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadNewsLetters();
  }

  private loadNewsLetters(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this._newsLetterService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.newsLetters = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.newsLetters = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading news letters:', error);
          this.newsLetters = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    this.paginationInfo = {
      currentPage: data.current_page || 1,
      perPage: data.per_page || 20,
      total: data.total || 0,
      lastPage: data.last_page || 1,
      firstPage: 1
    };
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  onPageChange(page: number): void {
    this.loadNewsLetters(page, this.searchTerm, this.filters);
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadNewsLetters(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = { search: '' };
    this.searchTerm = '';
    this.loadNewsLetters(1, '', {});
  }

  openAddNewsLetterModal(): void {
    this._newsLetterModalService.clearSelectedNewsLetter();
    const modalElement = document.getElementById('kt_modal_news_letter_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditNewsLetterModal(newsLetter: any): void {
    this._newsLetterModalService.setSelectedNewsLetter(newsLetter);
    const modalElement = document.getElementById('kt_modal_news_letter_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteNewsLetter(newsLetter: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete this news letter?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(newsLetter);
      }
    });
  }

  private performDelete(newsLetter: any): void {
    const id = newsLetter._id || newsLetter.id;
    this.subscriptions.push(
      this._newsLetterService.DeleteDataById(id).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'News letter has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadNewsLetters(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete news letter',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting news letter:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting news letter',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onNewsLetterAdded(): void {
    this.loadNewsLetters(this.currentPage, this.searchTerm, this.filters);
    this.newsLetterAdded.emit();
  }

  onNewsLetterUpdated(): void {
    this.loadNewsLetters(this.currentPage, this.searchTerm, this.filters);
    this.newsLetterUpdated.emit();
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  getAudienceLabels(audience: string[]): string {
    if (!audience || audience.length === 0) return '-';
    const labels: Record<string, string> = {
      'subscriber': 'Subscriber',
      'member': 'Member',
      'inquiry': 'Inquiry'
    };
    return audience.map(a => labels[a] || a).join(', ');
  }
}
